import React, { useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import { useDateUtils } from 'c-hooks';
import { formatNumberWhole } from 'c-lib';
import PostingStatusDot from './PostingStatusDot';
import type { PostingSort, PostingSortKey, StatusedPostingLine } from './lib';
import { initialSortDir, PostingStatus, sortPostingLines } from './lib';

const p = 'Modules.Main.Campaigns.ClassicPostings.Table';

const COLUMNS: Array<{ key: PostingSortKey; label: string; numeric?: boolean }> = [
    { key: 'name', label: 'unit' },
    { key: 'owner', label: 'owner' },
    { key: 'market', label: 'market' },
    { key: 'state', label: 'state' },
    { key: 'bookedStart', label: 'bookedStart' },
    { key: 'postedUp', label: 'posterUp' },
    { key: 'daysLate', label: 'daysLate', numeric: true },
    { key: 'impressions', label: 'impressions', numeric: true },
    { key: 'pct', label: 'vsExpected', numeric: true },
];

const None = () => (
    <Typography component="span" variant="body2" color="text.disabled">
        —
    </Typography>
);

type Props = {
    lines: StatusedPostingLine[];
    sort: PostingSort;
    onSortChange: (sort: PostingSort) => void;
};

const PostingLinesTable: React.FC<Props> = ({ lines, sort, onSortChange }) => {
    const t = useCommonTranslation();
    const { formatDateString } = useDateUtils();
    const sorted = useMemo(() => sortPostingLines(lines, sort), [lines, sort]);

    const onSort = (key: PostingSortKey) =>
        onSortChange({
            key,
            dir: sort.key === key ? (sort.dir === 1 ? -1 : 1) : initialSortDir(key),
        });

    const date = (d: string | null) => (d ? formatDateString({ date: d }) : <None />);

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="h6" mb={1.5}>
                    {t(`${p}.title`)}
                </Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 960 }}>
                        <TableHead>
                            <TableRow>
                                {COLUMNS.map(col => (
                                    <TableCell
                                        key={col.key}
                                        align={col.numeric ? 'right' : 'left'}
                                        sortDirection={
                                            sort.key === col.key
                                                ? sort.dir === 1
                                                    ? 'asc'
                                                    : 'desc'
                                                : false
                                        }
                                    >
                                        <TableSortLabel
                                            active={sort.key === col.key}
                                            direction={
                                                sort.key === col.key && sort.dir === 1
                                                    ? 'asc'
                                                    : 'desc'
                                            }
                                            onClick={() => onSort(col.key)}
                                        >
                                            {t(`${p}.${col.label}`)}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map(line => {
                                const waiting = line.status === PostingStatus.Awaiting;
                                return (
                                    <TableRow key={line.displayId} hover>
                                        <TableCell sx={{ maxWidth: 280 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {line.name || line.frameId}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {[
                                                    t(`${p}.frame`, { frame: line.frameId }),
                                                    line.postcode,
                                                    line.format,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{line.owner}</TableCell>
                                        <TableCell>{line.market}</TableCell>
                                        <TableCell>{line.state}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {date(line.bookedStart)}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {date(line.postedUp)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <PostingStatusDot
                                                status={line.status}
                                                label={
                                                    waiting
                                                        ? t(`${p}.awaiting`)
                                                        : line.daysLate > 0
                                                        ? t(`${p}.late`, { days: line.daysLate })
                                                        : t(`${p}.onDate`)
                                                }
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {waiting || line.impressions == null ? (
                                                <None />
                                            ) : (
                                                formatNumberWhole(line.impressions)
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {waiting || line.pct == null ? (
                                                <None />
                                            ) : (
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="flex-end"
                                                    gap={1}
                                                >
                                                    <Box width={54}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.min(100, line.pct)}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        minWidth={48}
                                                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                                                    >
                                                        {line.pct.toFixed(1)}%
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {!sorted.length && (
                                <TableRow>
                                    <TableCell colSpan={COLUMNS.length} align="center">
                                        <Typography variant="body2" color="text.secondary" py={2}>
                                            {t(`${p}.empty`)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

export default PostingLinesTable;
